import React, { useRef, useEffect } from "react";
import ScrollToBottomButton from "../Button/ScrollToBottomButton";
import ScrollToBottomStore from "../../store/ScrollToBottomStore";

/**
 * SparkLayout - Unified layout component for Spark chat interfaces
 * 
 * Enforces proper chat structure:
 * - Fixed header at top
 * - Scrollable messages area in middle (ONLY scroll container)
 * - Fixed prompt input at bottom
 * 
 * Prevents double-scroll issues by design.
 * 
 * @param {React.ReactNode} header - Header component (sticky at top)
 * @param {React.ReactNode} children - Messages content (scrollable area)
 * @param {React.ReactNode} prompt - Prompt/input component (fixed at bottom)
 * @param {string} className - Additional classes for the root container
 */
const SparkLayout = ({ header, children, prompt, className = "", scrollClassName = "", promptClassName = "" }) => {
  const promptWrapperRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const setShowScrollButton = ScrollToBottomStore((state) => state.setShowScrollButton);

  // Scroll detection - attached to the actual scroll container
  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollHeight, scrollTop, clientHeight } = scrollContainerRef.current;
        const isAtBottom = scrollHeight - scrollTop <= clientHeight + 50;
        setShowScrollButton(!isAtBottom);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      handleScroll(); // Check initial state
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [setShowScrollButton]);

  return (
    <div className={`flex flex-col h-full overflow-hidden ${className}`}>
      {/* Fixed Header - stays at top */}
      {header && (
        <div className="flex-shrink-0">
          {header}
        </div>
      )}

      {/* Scrollable Messages Area - ONLY vertical scroll container */}
      <div 
        id="spark-scroll-container"
        ref={scrollContainerRef}
        className={`flex-1 overflow-y-auto overflow-x-hidden flex flex-col [overflow-anchor:auto] ${scrollClassName}`}
      >
        {children}
      </div>

      {/* Fixed Prompt - stays at bottom */}
      {prompt && (
        <div ref={promptWrapperRef} className={`flex-shrink-0 relative overflow-visible ${promptClassName}`}>
          <ScrollToBottomButton anchorRef={promptWrapperRef} />
          {prompt}
        </div>
      )}
    </div>
  );
};

export default SparkLayout;
